/**
 * Microsoft Clarity 도입을 위한 실험용 기능.
 * dev / qa / prod 환경(로컬 dev 서버 제외)에서 앱 로드 시 항상 스크립트를 주입하고,
 * 로그인 이후 병원 ID와 employee id를 세션에 태깅한다.
 */

// 배포 환경별 Clarity 프로젝트 ID. 여기에 없는 환경(stg)에서는 init 하지 않는다.
const CLARITY_PROJECT_ID_BY_ENV: Partial<Record<'dev' | 'qa' | 'stg' | 'prod', string>> = {
  dev: 'xmot8vm4qp',
  qa: 'xmotlmqub1',
  prod: 'xmq2la5u9g',
};

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

let scriptInjected = false;

function loadClarityScript(projectId: string): void {
  if (scriptInjected) return;
  scriptInjected = true;

  const w = window as typeof window & { clarity?: ClarityFn };
  const clarity: ClarityFn =
    w.clarity ??
    (((...args: unknown[]) => {
      (clarity.q ??= []).push(args);
    }) as ClarityFn);
  w.clarity = clarity;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${projectId}`;
  document.head.appendChild(script);
}

/**
 * Clarity 스크립트를 주입한다. (앱 로드 시 항상 호출)
 * 배포된 dev / qa / prod 환경에서만 동작하며,
 * 로컬 dev 서버(import.meta.env.DEV)에서는 제외한다.
 */
export function initClarity(): void {
  // 로컬 dev 서버에서는 제외
  if (import.meta.env.DEV) return;

  const env = import.meta.env.VITE_ENV;
  const projectId = env ? CLARITY_PROJECT_ID_BY_ENV[env] : undefined;
  // 설정된 환경(dev/qa/prod) 외에는 init 하지 않는다.
  if (!projectId) return;

  loadClarityScript(projectId);
}

interface ClarityUser {
  employeeId?: number;
  orgzId?: number;
}

/**
 * 로그인 사용자 정보를 Clarity 세션에 태깅한다. (로그인 이후 호출)
 * Clarity가 로드되지 않은 환경에서는 아무 동작도 하지 않는다.
 */
export function identifyClarityUser({ employeeId, orgzId }: ClarityUser): void {
  // employee id로 세션을 사용자와 연결 (Clarity가 클라이언트에서 해시 처리)
  if (employeeId != null) {
    window.clarity?.('identify', String(employeeId));
  }
  // 병원 ID는 조직 식별 번호(비개인정보) → 필터용 태그
  if (orgzId != null) {
    window.clarity?.('set', 'orgzId', String(orgzId));
  }
}
